import invalidateCanvasConnections from '@/hooks/canvas/helpers/invalidateCanvasConnections';
import buildCanvasQueryKey from '@/hooks/canvas/helpers/buildCanvasQueryKey';
import type { SerializedCanvasConnection } from '@/hooks/canvas/types';
import coerceToRecord from '../helpers/coerceToRecord';
import eventMatchesToolNames from '../helpers/eventMatchesToolNames';
import getStringValue from '../helpers/getStringValue';
import type {
    BuildRefetchCanvasOnConnectionCreateSubscriptionParams,
    ChatEffectSubscription,
} from '../types';

const CANVAS_CONNECTION_CREATE_TOOL_NAMES = [
    'create_canvas_connection',
    'canvases.createConnection',
];

const buildRefetchCanvasOnConnectionCreateSubscription = (
    _params: BuildRefetchCanvasOnConnectionCreateSubscriptionParams
): ChatEffectSubscription => {
    return {
        id: 'refetch-canvas-on-connection-create',
        routePattern: '/agents/:agentId/canvas/:canvasId',
        eventType: 'TOOL_CALL_RESULT',
        filter: ({ event, matchParams }) => {
            if (
                !eventMatchesToolNames({
                    event,
                    toolNames: CANVAS_CONNECTION_CREATE_TOOL_NAMES,
                })
            ) {
                return false;
            }

            const args = coerceToRecord({ value: event.args });
            const canvasIdFromArgs = getStringValue({ value: args.canvasId });

            if (!canvasIdFromArgs) {
                return true;
            }

            return canvasIdFromArgs === matchParams.canvasId;
        },
        action: ({ event, queryClient, matchParams }) => {
            const connectionResult = coerceToRecord({ value: event.result });
            const nestedConnectionCandidate = coerceToRecord({
                value: connectionResult.data ?? connectionResult.connection,
            });
            const createdConnectionRecord =
                getStringValue({ value: nestedConnectionCandidate.connectionId }) !== ''
                    ? nestedConnectionCandidate
                    : connectionResult;
            const createdConnectionId = getStringValue({
                value: createdConnectionRecord.connectionId,
            });
            if (createdConnectionId) {
                queryClient.setQueryData(
                    buildCanvasQueryKey({ scope: 'connections', canvasId: matchParams.canvasId }),
                    (existingConnections: SerializedCanvasConnection[] | undefined) => {
                        const safeExistingConnections = existingConnections ?? [];
                        const alreadyExists = safeExistingConnections.some(
                            existingConnection =>
                                existingConnection.connectionId === createdConnectionId
                        );
                        if (alreadyExists) {
                            return safeExistingConnections;
                        }
                        return [
                            ...safeExistingConnections,
                            createdConnectionRecord as SerializedCanvasConnection,
                        ];
                    }
                );
            }
            invalidateCanvasConnections({
                queryClient,
                organizationId: matchParams.agentId,
                canvasId: matchParams.canvasId,
            });
        },
    };
};

export default buildRefetchCanvasOnConnectionCreateSubscription;
